import { ownAction, isActionCurrent } from '@/_core/fluxstore/dispatchOwnership';
import { Action } from '@/_core/fluxstore/types';
import { Activity, Task, StorymapState, Subtask } from '../types';
import { useStorymapStore } from '../useModule';
import { tryParseJSON, finishJSON } from '@/_core/utils/json';
import { ApiResponse, Goal, Step, Story } from '@/pages/aiStorymap/api/queries';
import * as storymapStoreModule from '../../store';
import { bugsnagNotify } from '@/_core/utils/bugsnag';

const { registerReducer, registerMiddleware, dispatch } = useStorymapStore();

export class PopulateAiStorymap extends Action<StorymapState> {
    public static ACTION_TYPE = 'PopulateAiStorymap';

    public constructor(public productDescription: string, public storymapId: string, public signal?: AbortSignal) {
        super(PopulateAiStorymap.ACTION_TYPE);
    }
}

const middleware = async (state: StorymapState, action: PopulateAiStorymap): Promise<boolean> => {
    const isCurrent = () => isActionCurrent(action) && !action.signal?.aborted &&
        useStorymapStore().state === state && state.id === action.storymapId;
    if (!isCurrent()) return false;
    const logger = createLogger('info');
    let reader: ReadableStreamDefaultReader<Uint8Array>;
    try {
        const response = await fetch(initAppData.appUrls.appUrl + '/api/ai/CreateStorymap', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ProductDescription: action.productDescription, StorymapId: action.storymapId, GenerateStoryDescriptions: true }),
            signal: action.signal,
        });
        reader = response.body.getReader();
        if (!isCurrent()) return false;
        const decoder = new TextDecoder('utf-8');
        let streamedJson = '';
        const addedGoalsActivities: { [key: string]: string } = {};
        const addedStepsTasks: { [key: string]: string } = {};
        const addedStoriesSubtasks: { [key: string]: string } = {};
        state.aiCreateStoryMapErrorMessage = '';
        let responseObj: ApiResponse = null;
        while (isCurrent()) {
            const { done, value } = await reader.read();
            if (!isCurrent()) return false;
            if (done) break;
            streamedJson += decoder.decode(value);
            responseObj = tryParseJSON(finishJSON(streamedJson));
            if (responseObj == null || responseObj.statusCode !== 200) continue;
            removeLastUnfinishedItemFromResponseTree(responseObj);
            await addNewCardsToStorymap(responseObj, state, addedGoalsActivities, addedStepsTasks, addedStoriesSubtasks, logger, isCurrent);
        }
        if (!isCurrent()) return false;
        if (responseObj == null || responseObj.statusCode !== 200) {
            state.aiCreateStoryMapErrorMessage = 'Error creating storymap with AI. ' + responseObj?.errorMessage;
            return false;
        }
        await saveAiProductData(responseObj, isCurrent);
        return isCurrent();
    } catch (error) {
        if (!isCurrent()) return false;
        throw error;
    } finally {
        if (reader && !isCurrent()) void reader.cancel().catch(() => undefined);
    }
};

function removeLastUnfinishedItemFromResponseTree(responseObj: ApiResponse) {
    const lastGoal = responseObj.goals ? responseObj.goals[responseObj.goals.length - 1] : null;
    const lastStep = lastGoal?.steps ? lastGoal.steps[lastGoal.steps.length - 1] : null;
    const lastStory = lastStep?.stories ? lastStep.stories[lastStep.stories.length - 1] : null;

    if (lastStory) {
        lastStep.stories.pop();
    } else if (lastStep) {
        lastGoal.steps.pop();
    } else if (lastGoal) {
        responseObj.goals.pop();
    }
}

async function addNewCardsToStorymap(
    responseObj: ApiResponse,
    state: StorymapState,
    addedGoalsActivities: { [key: string]: string },
    addedStepsTasks: { [key: string]: string },
    addedStoriesSubtasks: { [key: string]: string },
    logger: Logger,
    isCurrent: () => boolean
) {
    // state has state.activities (alias to Goals), state.tasks (steps), state.subtasks (stories) in flat arrays
    // tasks have reference to their parent activity, subtasks have reference to their parent task
    // responseObj has goals, steps, stories in nested arrays
    // we need to add new items to state.activities, state.tasks, state.subtasks from responseObj
    // we need to keep track of added goals to avoid duplicates
    // state.activities, state.tasks, state.subtasks will have id generated by the backend
    // responseObj will have id generated by the AI
    // so we need to map the id from responseObj to the id in state.activities, state.tasks, state.subtasks to keep track of the already added items
    // we need to add the new items to the state and keep track of the mapping between the ids

    //logger.debug('responseObj', responseObj);

    //responseObj.goals?.forEach(async (goal: Goal) => {
    for (const goal of responseObj.goals || []) {
        if (!isCurrent()) return;
        logger.debug('processing goal', goal, addedGoalsActivities);

        if (!addedGoalsActivities[goal.id] && goal.steps?.length > 0) {
            logger.debug('adding goal', goal.id);

            const action = new storymapStoreModule.AddActivityFromAi(goal.title, state.activities.length);
            await dispatch(ownAction(action, isCurrent));
            if (!isCurrent()) return;
            addedGoalsActivities[goal.id] = action._cardId;
            logger.debug('added goal', goal.id, action._cardId);
            //wait 100 ms to allow the new goal to be added to the state
            await new Promise((resolve) => setTimeout(resolve, 100));
        } else {
            logger.debug('goal already added', goal.id);
        }
        if (addedGoalsActivities[goal.id]) {
            //goal.steps.forEach(async (step: Step) => {
            for (const step of goal.steps || []) {
                if (!isCurrent()) return;
                logger.debug('processing step', step, addedStepsTasks);

                if (!addedStepsTasks[step.id] && step.stories?.length > 0) {
                    logger.debug('adding step', step.id);

                    const action = new storymapStoreModule.AddTaskFromAi(addedGoalsActivities[goal.id], step.title);
                    await dispatch(ownAction(action, isCurrent));
                    if (!isCurrent()) return;
                    addedStepsTasks[step.id] = action._cardId;
                    await new Promise((resolve) => setTimeout(resolve, 100));
                }
                if (addedStepsTasks[step.id]) {
                    //step.stories.forEach(async (story: Story) => {
                    for (const story of step.stories || []) {
                        if (!isCurrent()) return;
                        logger.debug('processing story', story, addedStoriesSubtasks);

                        if (!addedStoriesSubtasks[story.id]) {
                            logger.debug('adding story', story.id);

                            const action = new storymapStoreModule.AddSubtaskFromAi(
                                addedStepsTasks[step.id],
                                story.title,
                                null,
                                story.description
                            );
                            await dispatch(ownAction(action, isCurrent));
                            if (!isCurrent()) return;
                            addedStoriesSubtasks[story.id] = action._cardId;
                            await new Promise((resolve) => setTimeout(resolve, 100));
                        }
                    }
                }
            }
        }
    }
}

async function saveAiProductData(responseObj: ApiResponse, isCurrent: () => boolean) {
    if (!isCurrent()) return;
    if (responseObj.productType?.length > 0 || responseObj.productPurpose?.length > 0 || responseObj.keyFeatures?.length > 0) {
        const action = new storymapStoreModule.SaveAiDialog(
            null,
            responseObj.productType,
            responseObj.productPurpose,
            responseObj.keyFeatures?.join(',') ?? ',',
            null,
            null,
            null,
            responseObj.language ?? 'English'
        );

        await dispatch(ownAction(action, isCurrent));
        if (!isCurrent()) return;
    }
}

type Logger = {
    debug: (message: any, ...optionalParams: any[]) => void;
};

const createLogger = (level: string = 'debug'): Logger => {
    return {
        debug: (message: any, ...optionalParams: any[]) => {
            if (level === 'debug') {
                console.log(message, ...optionalParams);
            }
        },
    };
};

const reducer = (state: StorymapState, action: PopulateAiStorymap): void => {
    // @ts-ignore
};

//@ts-ignore
registerMiddleware(PopulateAiStorymap.ACTION_TYPE, middleware);

//@ts-ignore
registerReducer(PopulateAiStorymap.ACTION_TYPE, reducer);
